<template>
  <TableCell
    :width="`${width}px;`"
    class="table-cell-header fw-bold color-expinet-blue p-2 position-relative"
    @click="sort(sortBy)"
    @mouseover="showSortArrows = true"
    @mouseleave="showSortArrows = false"
  >
    <span v-if="!!tooltipId">
      <i
        :ref="tooltipId"
        class="position-relative fa fa-info-circle"
        @mouseover="displayTooltip(tooltipId)"
        @mouseleave="hideTooltip(tooltipId)"
      />
      {{ label }}
    </span>
    <span v-else>{{ label }}</span>
    <span 
      v-if="sortKey === sortBy"
      class="ms-1"
    >
      <i
        class="fa"
        :class="{
          'fa-sort-asc': sortOrder === 1,
          'fa-sort-desc': sortOrder === -1,
        }"
      />
    </span>
    <span 
      class="ms-1" 
      :class="{ invisible: !showSortArrows || sortKey === sortBy }"
    >
      <i class="fa fa-sort" />
    </span>
    <span 
      v-if="isPriceModelCell"
      class="mini-col position-absolute d-block text-end"
    >
      inkl. <i class="fa fa-plane" />
    </span>
  </TableCell>
</template>

<script lang="ts">
import { generateRequestId } from '@/general/helper'
import { useCatalogStore, useSortStore, useUiStore } from '@/store'
import { addTrackingEvent, buildSortTrackingEvent } from '@/store/tracking/helper'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import TableCell from './TableCell.vue'

export default defineComponent({
  components: {
    TableCell,
  },
  props: {
    label: { type: String, default: '' },
    width: { type: Number, default: 100 },
    sortBy: { type: String, default: '' },
    isPriceModelCell: { type: Boolean, default: false },
    tooltipId: { type: String, default: '' },
  },
  data: () => {
    return {
      showSortArrows: false,
    }
  },

  computed: {
    style() {
      return `width: ${this.width};`
    },
    ...mapState(useSortStore, {
      sortOrder: store => store.sortOrder,
    }),
    ...mapWritableState(useSortStore, {
      sortKey: 'sortKey',
    }),
    ...mapState(useCatalogStore, {
      requestId: store => store.requestId,
    }),
    ...mapWritableState(useCatalogStore, {
      pageNumber: 'pageNumber',
    }),
  },

  methods: {
    ...mapActions(useUiStore, {
      hideTooltip: 'hideTooltip',
      showTooltip: 'showTooltip'
    }),
    ...mapActions(useSortStore, {
      toggleSortOrder: 'toggleSortOrder',
    }),
    ...mapActions(useCatalogStore, {
      fetchCatalog: 'fetchCatalog',
    }),

    async sort(key: string) {
      if (this.sortKey !== key) {
        this.sortKey = key
      } else {
        this.toggleSortOrder()
      }

      this.pageNumber = 1
      this.requestId = generateRequestId('FETCAT')

      await this.fetchCatalog()
      this.addSortTrackingEvent()
    },

    addSortTrackingEvent() {
      const event = buildSortTrackingEvent(this.sortKey, this.sortOrder)
      addTrackingEvent(event, 'Sort Tracking Event')
    },

    displayTooltip(name: string) {
      const rect = (this.$refs[name] as HTMLElement).getBoundingClientRect()
      
      // TODO: make this dynamic
      const pos = { x: this.getPosition(name, rect.left), y: rect.top }

      this.showTooltip({ name, pos })
    },

    getPosition: (name: string, position: number) => {
      switch(name) {
        case 'cruise-list-header-icon-specials':
          return position - 735
        case 'cruise-list-header-icon-classic-all-inclusive':
          return position - 495
        case 'cruise-list-header-icon-classic':
          return position - 390
        default:
          return position
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.cell {
  display: inline-block;
  vertical-align: top;
  white-space: normal;
}

.table-cell-header {
  height: $panel-header-height;
  cursor: pointer;
}

.mini-col {
  right: 14px;
  bottom: -5px;
  font-size: 10px;
}
</style>
